'use client';
import React, { useEffect, useRef, useState } from 'react';

const credentialCards = [
  {
    icon: '📄',
    title: 'Certified Résumé Writers',
    desc: 'Our team holds professional certifications in résumé writing, ensuring every document meets the highest industry standards.',
  },
  {
    icon: '🌍',
    title: 'Global Job Market Knowledge',
    desc: 'We help job seekers navigate local and international job markets with tailored strategies that reflect current hiring trends.',
  },
  {
    icon: '🎯',
    title: '15+ Years Career Coaching',
    desc: 'Over a decade and a half of dedicated career coaching experience, helping professionals at every stage of their career journey.',
  },
  {
    icon: '👔',
    title: 'Recruiter & HR Insight',
    desc: 'Our coaches bring firsthand experience from inside recruitment and HR, giving clients a genuine insider advantage.',
  },
  {
    icon: '🎓',
    title: 'Interview Coaching Specialists',
    desc: 'Structured interview preparation programs that build confidence, sharpen responses, and dramatically improve offer rates.',
  },
  {
    icon: '🏆',
    title: '94% Interview Success Rate',
    desc: 'Our clients consistently report securing interviews within weeks of working with us, a track record built on results.',
  },
];

export default function ProcessHero() {
  const [visible, setVisible] = useState(false);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (cardsRef?.current) observer?.observe(cardsRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="hero-bg relative pt-40 pb-24 overflow-hidden" id="process-hero">
        <div className="noise-overlay" />
        <div className="hero-scan" />
        <div
          className="absolute inset-0 pointer-events-none opacity-5"
          style={{
            backgroundImage: 'radial-gradient(rgba(201,168,76,0.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at 50% 50%, black, transparent 80%)',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div
            className="flex items-center gap-3 mb-10"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 0.2s' }}
          >
            <span className="font-mono-custom text-xs tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
              Our Expertise &amp; Approach
            </span>
            <div className="gold-rule" />
          </div>

          <h1
            className="font-fraunces font-light mb-6"
            style={{
              fontSize: 'clamp(2.8rem, 7vw, 6.5rem)',
              lineHeight: '0.95',
              letterSpacing: '-0.03em',
              color: '#F5F0E8',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s',
            }}
          >
            Why Choose<br />
            <span style={{ color: '#C9A84C', fontStyle: 'italic' }}>Betty Berkely?</span>
          </h1>

          <div className="mt-8 mb-10 gold-rule-full opacity-30" />

          <p
            className="max-w-2xl text-lg font-light leading-relaxed mb-10"
            style={{
              color: 'rgba(245,240,232,0.65)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.8s ease 0.7s, transform 0.8s ease 0.7s',
            }}
          >
            Betty Berkely and her team combine deep career coaching expertise, certified résumé writing skills, and genuine insight into what employers are looking for, giving every job seeker a meaningful competitive edge.
          </p>

          <div
            className="pull-quote pl-5 py-2 max-w-md"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s ease 1s' }}
          >
            <p className="font-fraunces text-base font-light italic" style={{ color: 'rgba(245,240,232,0.8)' }}>
              "We don't just write résumés. We help you build a career strategy that works."
            </p>
            <p className="text-xs font-mono-custom mt-2" style={{ color: 'rgba(201,168,76,0.6)' }}>Betty Berkely, Founder</p>
          </div>
        </div>
        <div
          className="absolute bottom-0 inset-x-0 h-24 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(74,25,66,0.6))' }}
        />
      </section>

      {/* Credentials grid */}
      <section className="section-cream relative py-24 lg:py-32 overflow-hidden" id="credentials">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div ref={cardsRef} className="reveal-section mb-12">
            <span className="section-index block mb-4" style={{ color: 'rgba(74,25,66,0.4)' }}>
              OUR EXPERTISE
            </span>
            <h2
              className="font-fraunces font-medium"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: '#4A1942', lineHeight: '1.1' }}
            >
              The Expertise Behind the Results
            </h2>
          </div>

          {/* Bento-style asymmetric grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {credentialCards?.map((card, i) => (
              <div
                key={i}
                className="process-card-light rounded-sm p-7 transition-all duration-300"
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <div className="text-3xl mb-4">{card?.icon}</div>
                <h3 className="font-semibold text-base mb-2" style={{ color: '#4A1942' }}>{card?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6A6A6A' }}>{card?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
